import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';

const CATEGORIES = ['Checking', 'Savings', 'Credit Card', 'Investment', 'Loan', 'Real Estate', 'Vehicle', 'Accounts Receivable', 'Accounts Payable', 'Other', ''];

function AccountBalances({ refreshKey, extraRefreshKey }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', balance: '', currency: 'USD', category: '' });

  useEffect(() => {
    fetchAccounts();
  }, [refreshKey, extraRefreshKey]);

  async function fetchAccounts() {
    try {
      const res = await apiFetch('/api/simplefin/accounts');
      if (res.ok) setAccounts(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function formatCurrency(amount, currency) {
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    if (currency === 'USD') {
      return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }
    return `${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  }

  const totalBalance = useMemo(() => {
    return accounts.reduce((sum, a) => sum + parseFloat(a.balance || 0), 0);
  }, [accounts]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const a of accounts) {
      const cat = a.category || 'Uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(a);
    }
    // Sort categories: known ones first, then Uncategorized
    const order = ['Checking', 'Savings', 'Credit Card', 'Investment', 'Real Estate', 'Vehicle', 'Accounts Receivable', 'Accounts Payable', 'Loan', 'Other', 'Uncategorized'];
    return order
      .filter((c) => groups[c])
      .map((c) => ({
        category: c,
        accounts: groups[c],
        total: groups[c].reduce((s, a) => s + parseFloat(a.balance || 0), 0),
      }));
  }, [accounts]);

  async function updateCategory(accountId, category) {
    try {
      await apiFetch(`/api/simplefin/accounts/${encodeURIComponent(accountId)}/category`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category }),
      });
      setAccounts((prev) => prev.map((a) => (a.id === accountId ? { ...a, category } : a)));
    } catch (err) {
      console.error(err);
    }
  }

  function startAdd() {
    setForm({ name: '', balance: '', currency: 'USD', category: '' });
    setShowAddForm(true);
    setEditingId(null);
  }

  function startEdit(a) {
    setForm({ name: a.name, balance: a.balance, currency: a.currency, category: a.category || '' });
    setEditingId(a.id);
    setShowAddForm(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      if (editingId) {
        await apiFetch(`/api/simplefin/accounts/manual/${encodeURIComponent(editingId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
      } else {
        await apiFetch('/api/simplefin/accounts/manual', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
      }
      setShowAddForm(false);
      setEditingId(null);
      fetchAccounts();
    } catch (err) {
      console.error(err);
    }
  }

  async function handleDelete(accountId) {
    if (!window.confirm('Delete this manual account?')) return;
    try {
      await apiFetch(`/api/simplefin/accounts/manual/${encodeURIComponent(accountId)}`, {
        method: 'DELETE',
      });
      fetchAccounts();
    } catch (err) {
      console.error(err);
    }
  }

  if (loading) {
    return (
      <section className="card">
        <h2>Account Balances</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  if (accounts.length === 0 && !showAddForm) {
    return (
      <section className="card">
        <div className="goals-header">
          <h2>Account Balances</h2>
          <button className="sfin-add-btn" onClick={startAdd}>+ Manual</button>
        </div>
        <p className="receipts-empty">No accounts synced yet. Use the SimpleFIN panel to sync.</p>
      </section>
    );
  }

  return (
    <section className="card sfin-balances-section">
      <div className="goals-header">
        <h2>Account Balances</h2>
        <div className="sfin-header-actions">
          <button className="sfin-add-btn" onClick={startAdd}>+ Manual</button>
          <span className="sfin-total">{formatCurrency(totalBalance, 'USD')}</span>
        </div>
      </div>

      {showAddForm && (
        <form className="sfin-manual-form" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Account name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <input
            type="number"
            step="0.01"
            placeholder="Balance"
            value={form.balance}
            onChange={(e) => setForm({ ...form, balance: e.target.value })}
            required
          />
          <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
            <option value="CAD">CAD</option>
          </select>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="">No Category</option>
            {CATEGORIES.filter(Boolean).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <div className="sfin-form-actions">
            <button type="submit">{editingId ? 'Save' : 'Add'}</button>
            <button type="button" onClick={() => { setShowAddForm(false); setEditingId(null); }}>Cancel</button>
          </div>
        </form>
      )}

      <div className="sfin-accounts-list">
        {grouped.map((g) => (
          <div key={g.category} className="sfin-category-group">
            <div className="sfin-category-header">
              <span className="sfin-category-name">{g.category}</span>
              <span className="sfin-category-total">{formatCurrency(g.total, 'USD')}</span>
            </div>
            {g.accounts.map((a) => {
              const bal = parseFloat(a.balance || 0);
              const isNegative = bal < 0;
              return (
                <div key={a.id} className="sfin-account-row">
                  <div className="sfin-account-info">
                    <span className="sfin-account-name">
                      {a.name}
                      {a.isManual && <span className="sfin-manual-badge">manual</span>}
                    </span>
                    <div className="sfin-account-meta">
                      <select
                        className="sfin-category-select"
                        value={a.category || ''}
                        onChange={(e) => updateCategory(a.id, e.target.value)}
                      >
                        <option value="">No Category</option>
                        {CATEGORIES.filter(Boolean).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      {a.balanceDate && (
                        <span className="sfin-account-date">
                          {new Date(a.balanceDate).toLocaleDateString('en-US', {
                            timeZone: 'America/Chicago',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      )}
                      {a.isManual && (
                        <span className="sfin-manual-actions">
                          <button className="sfin-edit-btn" onClick={() => startEdit(a)} title="Edit">&#9998;</button>
                          <button className="sfin-delete-btn" onClick={() => handleDelete(a.id)} title="Delete">&times;</button>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="sfin-account-balance-col">
                    <span className={`sfin-account-balance${isNegative ? ' negative' : ''}`}>
                      {formatCurrency(a.balance, a.currency)}
                    </span>
                    {a.availableBalance && a.availableBalance !== a.balance && (
                      <span className="sfin-account-available">
                        {formatCurrency(a.availableBalance, a.currency)} avail
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
}

export default AccountBalances;
